import React, { useMemo } from "react";
import type { SummaryCardConfig } from "../constants";
import { STATUS_CONFIG } from "../constants";

interface SingleSummaryCardProps {
  config: SummaryCardConfig;
  programs: any[];
  invertTrend?: boolean;
}

export const SingleSummaryCard: React.FC<SingleSummaryCardProps> = ({
  config: cfg, programs, invertTrend,
}) => {
  const { value, delta, cumValue, details } = useMemo(() => {
    if (programs.length === 0) return { value: null, delta: null, cumValue: "-", details: [] };

    const p = programs[0];
    const rawVal = p[cfg.valueField];
    const rawPrev = p[cfg.prevField];
    const val = rawVal != null ? rawVal * 100 : null;
    const prev = rawPrev != null ? rawPrev * 100 : null;

    const cumRaw = p[cfg.cumField];
    const cum = cumRaw != null ? cumRaw.toLocaleString() : "--";

    const det = cfg.details.map((d) => {
      const raw = p[d.field!];
      if (raw == null) return { label: d.label, value: "--" };
      if (d.showGapColor) {
        return { label: d.label, value: `${raw >= 0 ? "+" : ""}${raw.toLocaleString()}`, color: raw >= 0 ? "#1d8102" : "#d13212" };
      }
      const v = d.percent ? `${(raw * 100).toFixed(2)}%` : raw.toLocaleString();
      return { label: d.label, value: v };
    });

    return { value: val, delta: val != null && prev != null ? val - prev : null, cumValue: cum, details: det };
  }, [cfg, programs]);

  const programStatus = programs.length > 0 ? programs[0].status : undefined;
  const statusCfg = programStatus ? STATUS_CONFIG[programStatus] : undefined;
  const valueColor = statusCfg ? statusCfg.color : (value != null && value >= cfg.target ? "#1d8102" : "#d13212");

  const trendUp = delta != null && delta >= 0;
  const trendPositive = invertTrend ? !trendUp : trendUp;
  const trendColor = trendPositive ? "#1d8102" : "#d13212";

  return (
    <div data-testid="single-summary-card" className="summary-card-wrapper">
      <div className="summary-card-main">
        <div className="summary-card-value-group">
          {value == null ? (
            <span className="summary-card-value" style={{ fontSize:'18px',color: "#aab7b8" }}>——</span>
          ) : (
            <>
              <span className="summary-card-value" data-testid="summary-percentage" style={{ color: valueColor }}>
                {value.toFixed(1)}<span className="summary-card-value-unit">%</span>
              </span>
              <span className="summary-card-trend" data-testid="summary-trend" data-trend-direction={trendUp ? "up" : "down"} style={delta != null ? { color: trendColor } : undefined}>
                {delta != null ? <>{trendUp ? "▲+" : "▼"}{delta.toFixed(2)}%</> : "——"}
              </span>
            </>
          )}
        </div>
        <div className="summary-card-cum">
          <div className="stage-detail" data-testid="summary-cum-label">{cfg.cumLabel}</div>
          <div className="card-cum-value" data-testid="summary-cum-value">{cumValue}</div>
        </div>
      </div>
      <div className="summary-card-divider">
        <div className="summary-card-details" data-testid="summary-details">
          {details.map((d) => (
            <div key={d.label} className="summary-card-detail-item" data-testid={`summary-detail-${d.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="summary-card-detail-label">{d.label}</div>
              <div className="summary-card-detail-value" style={{ color: d.color || "#16191f" }}>{d.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
